import React from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartOptions,
  Plugin,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import StarRating from "../../../recommendation/textRecommendation/components/ratingStar";
import CommonUtils from "../../../../utils/Common_Utils";
import { AssessmentData } from "../interface/ScoreResponse_Interface";
import "./../styles/SkillScoreStyle.css";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Custom Plugin for drawing labels over bars
const customLabelPlugin: Plugin = {
  id: "customLabelPlugin",
  afterDatasetDraw: (chart) => {
    const { ctx, data, scales } = chart;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta.hidden) {
        meta.data.forEach((bar, index) => {
          const { x, y } = bar.getProps(["x", "y"]);
          const value = dataset.data[index]; // Value to display as label

          // Ensure value is a valid number and not an array or other type
          if (typeof value === "number" && !isNaN(value)) {
            let percentage = `${value}%`; // Default format as percentage

            // Check if value is too small and replace it with 0%
            if (value < 1.47) {
              percentage = "0%";
            }

            ctx.save();
            ctx.font = "bold 15px Mazzard";
            ctx.fillStyle = "#414141"; // Text color for the labels
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            ctx.fillText(percentage, x, y - 5); // Adjust the position of the label above the bar
            ctx.restore();
          }
        });
      }
    });
  },
};

ChartJS.register(customLabelPlugin); // Register the custom plugin

ChartJS.register(customLabelPlugin); // Register the custom plugin

interface BarChartComponentProps {
  datas: AssessmentData;
}

const BarChartComponent: React.FC<BarChartComponentProps> = ({ datas }) => {
  const dimensionOrder = [
  "Learning & Innovation",
  "Life & Career",
  "Information & Media Technology",
  "Interdisciplinary",
];

const orderedDimensions = dimensionOrder
  .map((name) => datas.skill_report.find((d) => d.dimension_name === name))
  .filter(Boolean) as typeof datas.skill_report; // cast to array of Dimension

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "20px",
        // padding: "25px",
        // paddingRight: "25px",
        marginRight: "15px",
        // marginTop:"20px",
        borderRadius: "10px",
        // backgroundColor:"blue"
      }}
    >
      {orderedDimensions.map((dimension) => {
       // Sort skill_score array alphabetically based on skill name
const sortedSkills = [...dimension.skill_score].sort((a, b) => {
  const nameA = CommonUtils.getSkillName(a.skill_name).toLowerCase();
  const nameB = CommonUtils.getSkillName(b.skill_name).toLowerCase();
  return nameA.localeCompare(nameB);
});
const backgroundColorsbar = sortedSkills.map((skill) =>
  CommonUtils.getBarClass(skill.score)
);

// Extract sorted labels, scores, and colors
const labels = sortedSkills.map((skill) =>
  CommonUtils.getSkillName(skill.skill_name)
);
const scores = sortedSkills.map((skill) => (skill.score === 0 ? 1.46 : skill.score));
const backgroundColors = sortedSkills.map((skill) =>
  CommonUtils.getBarClassbar(skill.score)
);


        const data = {
          labels,
          datasets: [
            {
              label: "Score (%)",
              data: scores,
              backgroundColor: backgroundColors,
            },
          ],
        };

        const options: ChartOptions<"bar"> = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              enabled: true,
              callbacks: {
                title: () => "",
                label: (context) =>
                  `${CommonUtils.getToolTipName(context.label)}`,
              },
              position: "nearest",
              yAlign: "bottom",
              xAlign: "center",
              backgroundColor: "black",
              titleColor: "white",
              bodyColor: "white",
              titleFont: {
                family: "Mazzard",
                size: 13,
              },
              bodyFont: {
                family: "Mazzard",
                size: 13,
              },
              padding: 10,
              displayColors: false,
              caretPadding: 10,
            },
            legend: {
              display: false,
            },
          },
          onHover: (event, elements) => {
            // Access the canvas element
            const chartCanvas = event.native?.target as HTMLCanvasElement;
            if (elements.length) {
              // Set cursor to pointer when hovering over a bar
              chartCanvas.style.cursor = "pointer";
            } else {
              // Reset cursor when not hovering over a bar
              chartCanvas.style.cursor = "default";
            }
          },
          scales: {
            x: {
              ticks: {
                font: {
                  family: "Mazzard",
                  size: 13,
                  weight: "bold",
                },
                color: "#414141",
                autoSkip: false,
                maxRotation: 0,
                minRotation: 0,
                callback: (value: string | number) => {
                  const index =
                    typeof value === "number" ? value : Number(value); // Ensure value is treated as a number
                  const label = labels[index]; // Access the label by index
                  const maxLength = 10; // Set the maximum length of the label

                  if (label && label.length > maxLength) {
                    return label.substring(0, maxLength) + "..."; // Truncate and add ellipsis
                  }
                  return label || ""; // Ensure the return type is string, not String object
                },
              },

              grid: {
                display: false,
                drawOnChartArea: false,
                drawTicks: true,
              },
            },
            y: {
              max: 120,
              ticks: {
                stepSize: 20,
                font: {
                  family: "Mazzard",
                  size: 13,
                },
              },
              grid: {
                drawOnChartArea: true,
                drawTicks: false,
              },
            },
          },
        };

      return (
  <div
    key={dimension.dimension_id}
    style={{
      position: "relative",
      height: "350px",
      width: "100%",
      backgroundColor:
        CommonUtils.getBgAndColorBasedScore(dimension.dimension_score)?.color,
      borderRadius: "10px",
      overflow: "hidden",
      padding: "20px",
    }}
  >
    {/* Background Image */}
    <img
      src={
        CommonUtils.getBgAndColorBasedScore(dimension.dimension_score)
          ?.chartBgImageUrl
      }
      alt="Background"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        opacity: 0.15,
        zIndex: 0,
      }}
    />

    {/* Title */}
    <div
      style={{
        position: "absolute",
        top: "15px",
        left: "20px",
        zIndex: 2,
      }}
    >
      <span
        style={{
          fontSize: "18px",
          fontWeight: "600",
          color: "#414141",
          fontFamily: "Mazzard",
        }}
      >
        {dimension.dimension_name}
      </span>
    </div>

    {/* Rating */}
    <div
      style={{
        position: "absolute",
        top: "15px",
        right: "20px",
        zIndex: 2,
      }}
    >
      <StarRating rating={dimension.dimension_score} />
    </div>

    {/* Chart */}
    <div
      style={{
        position: "relative",
        zIndex: 2,
        marginTop: "50px",
        height: "260px",
      }}
    >
      <Bar
        data={{
          labels: sortedSkills.map((skill) =>
            CommonUtils.getSkillName(skill.skill_name)
          ),
          datasets: [
            {
              label: "Score",
              data: sortedSkills.map((skill) =>
                skill.score === 0 ? 1.46 : skill.score
              ),
 
    backgroundColor: backgroundColors,
                borderRadius: 6,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          layout: {
    padding: {
      top: 25,
    },
  },
          plugins: {
            legend: {
              display: false,
            },
          },
         scales: {
  y: {
    beginAtZero: true,
    max: 100,
    grid: {
      display: true,
    color: "#D9D9D9",
    lineWidth: 1,
    drawTicks: false,
    },
    
  },

  x: {
    ticks: {
      maxRotation: 0,
      minRotation: 0,
    },
    grid: {
      display: false, // Hide vertical lines
      drawOnChartArea: false,
      drawTicks: true,
    },
  },
},
        }}
      />
    </div>
  </div>
);
      })}
    </div>
  );
};

export default BarChartComponent;
